/**
 * Canonical Watch journey configuration — single source of truth.
 *
 * FROZEN 6-chapter Watch IA (v1, Phase 1+2):
 *   0 Winding  — /cast        (entry, no completion state)
 *   1 Face     — /reading     (natal foundation; Avasthas folded in)
 *   2 Movement — /movement    (Situation|Ages ledger + 5-horizon journey
 *                              aggregator; Predictions/Today merged in)
 *   4 Council  — /council     (council / three-council)
 *   5 Sound    — /life-music  (/radio alias)
 *   6 Sky      — /sky         (/mundane folded in as a section)
 *
 * OUT (legacy, not chapters): Journey-as-chapter (3), Predictions-as-
 * chapter (7), Archive-as-chapter (→ Discover), Avasthas-as-chapter
 * (→ Face section), Mundane-as-chapter (→ Sky section).
 *
 * Legacy routes stay addressable: each legacy chapter carries a redirect
 * target, and its page renders a banner pointing at the canonical home.
 * Ids 3 and 7 are RETIRED — never shown on the bezel or the pathway —
 * but remain in the JourneyChapterId union so stored progress migrates
 * instead of crashing lookups.
 *
 * Personal vs demo separation: the `unavailableReason` predicate returns a
 * human-readable reason when a chapter cannot be opened in the current
 * context (no chart id → "Available after casting").
 *
 * Privacy: no PII fields. No chart ids in the public-facing labels.
 */

import { isDemoChart } from "./entitlements";
import { LOCALES } from "../i18n/locales";

/**
 * 0=Winding, 1=Face, 2=Movement, 4=Council, 5=Sound, 6=Sky are canonical.
 * 3=Journey and 7=Predictions are RETIRED legacy ids (redirect + banner).
 */
export type JourneyChapterId = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** Canonical (bezel) chapter ids only. */
export type WatchChapterId = 0 | 1 | 2 | 4 | 5 | 6;

/** Retired chapter ids — redirect targets, never bezel destinations. */
export type LegacyChapterId = 3 | 7;

export interface JourneyChapter {
  id: WatchChapterId;
  /** Roman or numeric mark shown on the bezel. */
  mark: string;
  /** User-facing short label. */
  name: string;
  /** Plain-language description for first-time visitors. */
  plain: string;
  /** Base route (without chart id). */
  baseRoute: string;
  /** Whether the route requires a chart id in the URL. */
  needsChart: boolean;
  /** Builds the canonical route for the given context. */
  routeBuilder: (chartId: string | null) => string;
  /** Returns a reason string if the chapter is unavailable, else null. */
  unavailableReason: (chartId: string | null) => string | null;
  /**
   * Returns the recommended next chapter given the current chart.
   * null means "no recommendation" (end of the chain, or no chart).
   *
   * Canonical progression: Face → Movement → Council → Sound → Sky.
   */
  recommendedNext: (chartId: string | null) => WatchChapterId | null;
}

export interface LegacyChapter {
  id: LegacyChapterId;
  /** Historic bezel mark (retired — never rendered on the bezel). */
  mark: string;
  name: string;
  plain: string;
  /** Retired base route (still addressable, shows a redirect banner). */
  baseRoute: string;
  /** Canonical base route the banner points at. */
  redirectBase: string;
  routeBuilder: (chartId: string | null) => string;
  /** Canonical destination for the given context. */
  redirectBuilder: (chartId: string | null) => string;
  /**
   * Retired chapters recommend their canonical home, so generic
   * next-step consumers keep working without legacy branches.
   */
  recommendedNext: (chartId: string | null) => WatchChapterId | null;
}

const ROOT = "/cast";

const buildRoute = (base: string, needsChart: boolean) =>
  (chartId: string | null): string => {
    if (!needsChart) return base;
    if (!chartId) return ROOT;
    return `${base}/${chartId}`;
  };

const unavailableWhenNoChart = (chartId: string | null): string | null =>
  chartId ? null : "Available after casting — cast your chart first";

/**
 * Frozen canonical chapters — ORDER MATTERS (Winding first).
 *
 * Marks are the PUBLIC bezel marks (0, I..V); legacy numeric ids are
 * intentionally NOT reused so stored progress keeps its meaning.
 */
export const JOURNEY_CHAPTERS: readonly JourneyChapter[] = [
  {
    id: 0,
    mark: "0",
    name: "Winding",
    plain: "Tell us when and where you were born. We calculate your chart.",
    baseRoute: "/cast",
    needsChart: false,
    routeBuilder: () => "/cast",
    unavailableReason: () => null,
    recommendedNext: (chartId) => (chartId ? 1 : null),
  },
  {
    id: 1,
    mark: "I",
    name: "Face",
    plain: "Your natal chart: ascendant, planets, houses — the foundation.",
    baseRoute: "/reading",
    needsChart: true,
    routeBuilder: buildRoute("/reading", true),
    unavailableReason: unavailableWhenNoChart,
    recommendedNext: (_chartId) => 2,
  },
  {
    id: 2,
    mark: "II",
    name: "Movement",
    plain: "A life-period ledger, current age and key transitions.",
    baseRoute: "/movement",
    needsChart: true,
    routeBuilder: buildRoute("/movement", true),
    unavailableReason: unavailableWhenNoChart,
    recommendedNext: (_chartId) => 4,
  },
  {
    id: 4,
    mark: "III",
    name: "Council",
    plain: "Twelve classical traditions weigh in, side by side.",
    baseRoute: "/council",
    needsChart: true,
    routeBuilder: buildRoute("/council", true),
    unavailableReason: unavailableWhenNoChart,
    recommendedNext: (_chartId) => 5,
  },
  {
    id: 5,
    mark: "IV",
    name: "Sound",
    plain: "The chart as music — a raga for each life period.",
    // THE FROZEN ROUTE, AND DELIBERATELY SO. Plan C3, final state.
    //
    // Every LINK to the music now leads to `/station`, which synthesises on the
    // device: the Listen tab's two modes, the landing music card, the dasha-period
    // card and the journey map. This chapter's own canonical URL stays
    // `/life-music`, and that is a decision rather than an oversight.
    //
    // `journey.test.ts` freezes it -- "/life-music is the Sound chapter (id 5)",
    // asserted on every locale prefix -- and a frozen contract is not lifted
    // because an instruction suggests it. Repointing a Watch chapter's canonical
    // URL changes what a bookmark and a shared link resolve to, which is the
    // owner's call and was not given.
    //
    // It is also defensible: ADR-017 section 4 permits the server-rendered path
    // for a public surface, and this is a deep link into the Sound chapter
    // rather than a door into the product. Every door leads to the station.
    baseRoute: "/life-music",
    needsChart: true,
    routeBuilder: buildRoute("/life-music", true),
    unavailableReason: unavailableWhenNoChart,
    recommendedNext: (_chartId) => 6,
  },
  {
    id: 6,
    mark: "V",
    name: "Sky",
    plain: "Current transits overlaid on your natal chart.",
    baseRoute: "/sky",
    needsChart: true,
    routeBuilder: buildRoute("/sky", true),
    unavailableReason: unavailableWhenNoChart,
    recommendedNext: (_chartId) => null,
  },
] as const;

/**
 * Retired chapters. Addressable legacy routes with redirect + banner —
 * never bezel or pathway destinations.
 */
export const LEGACY_CHAPTERS: readonly LegacyChapter[] = [
  {
    id: 3,
    mark: "III",
    name: "Journey",
    plain: "Retired: the dasha timeline now lives inside Movement.",
    baseRoute: "/journey",
    redirectBase: "/movement",
    routeBuilder: buildRoute("/journey", true),
    redirectBuilder: buildRoute("/movement", true),
    recommendedNext: (_chartId) => 2,
  },
  {
    id: 7,
    mark: "VII",
    name: "Predictions",
    plain: "Retired: topic readings continue as Movement Time-Discover entries.",
    baseRoute: "/predictions",
    redirectBase: "/movement",
    routeBuilder: buildRoute("/predictions", true),
    redirectBuilder: buildRoute("/movement", true),
    recommendedNext: (_chartId) => 2,
  },
] as const;

/**
 * Legacy base-route → canonical base-route redirects (chart id preserved
 * by the caller). /archive leaves the Watch for Discover; /mundane folds
 * into the Sky chapter.
 */
export const LEGACY_ROUTE_REDIRECTS: Readonly<Record<string, string>> = {
  "/journey": "/movement",
  "/predictions": "/movement",
  "/mundane": "/sky",
  "/archive": "/discover",
};

/** Number of canonical chapters (entry + 5 destinations). */
export const JOURNEY_CHAPTER_COUNT = JOURNEY_CHAPTERS.length;

/** Convenience map id → chapter for O(1) lookup (canonical + legacy). */
const _BY_ID = new Map<JourneyChapterId, JourneyChapter | LegacyChapter>([
  ...(JOURNEY_CHAPTERS.map((c) => [c.id, c] as const)),
  ...(LEGACY_CHAPTERS.map((c) => [c.id, c] as const)),
]);

/** The public Watch bezel: exactly the six frozen canonical chapters. */
export const WATCH_CHAPTER_IDS: readonly WatchChapterId[] = [0, 1, 2, 4, 5, 6] as const;
export const WATCH_CHAPTERS: readonly JourneyChapter[] = WATCH_CHAPTER_IDS.map(
  (id) => _BY_ID.get(id)! as JourneyChapter,
);

/** Display marks for the six-chapter public Watch, independent of legacy IDs. */
const WATCH_DISPLAY_MARKS = ["0", "I", "II", "III", "IV", "V"] as const;

export function watchChapterMark(id: JourneyChapterId): string {
  const index = (WATCH_CHAPTER_IDS as readonly JourneyChapterId[]).indexOf(id);
  if (index >= 0) return WATCH_DISPLAY_MARKS[index]!;
  return _BY_ID.get(id)?.mark ?? "?";
}

export function getJourneyChapter(id: JourneyChapterId): JourneyChapter | LegacyChapter {
  return _BY_ID.get(id) as JourneyChapter | LegacyChapter;
}

/** True for the retired ids 3 (Journey) and 7 (Predictions). */
export function isLegacyChapter(id: JourneyChapterId): id is LegacyChapterId {
  return id === 3 || id === 7;
}

/**
 * Map a pathname to the chapter id it belongs to, or -1 if none.
 * Used by ChapterNav to highlight the active mark on the bezel.
 *
 * /watch (the overview landing) belongs to NO chapter by design.
 * Legacy /journey and /predictions still resolve to their retired ids so
 * pages can render the redirect banner; the bezel never highlights them
 * because they are not in WATCH_CHAPTERS.
 */
/** Locale codes usable as a URL path prefix. Mirrors src/i18n/locales.ts. */
const LOCALE_PATH_PREFIXES = new Set(
  LOCALES.map((l) => l.code.toLowerCase()),
);

export function chapterForPathname(pathname: string): JourneyChapterId | -1 {
  // Locales are addressed by path prefix (/hi/movement), so the first segment
  // is the locale on 11 of 12 locales and the route on the unprefixed one.
  // Reading segment 1 unconditionally meant "/hi" was compared against the
  // chapter base routes, so no chapter ever matched and the bezel lost both
  // its active-chapter highlight and its passed state.
  const parts = pathname.split("/").filter(Boolean);
  const first = parts[0] ?? "";
  const isLocale = LOCALE_PATH_PREFIXES.has(first.toLowerCase());
  const rest = isLocale ? parts.slice(1) : parts;
  const seg = "/" + (rest[0] ?? "");
  for (const c of JOURNEY_CHAPTERS) {
    if (c.baseRoute === seg) return c.id;
  }
  for (const c of LEGACY_CHAPTERS) {
    if (c.baseRoute === seg) return c.id;
  }
  // Sub-pages under a chapter map to their parent chapter.
  if (
    seg === "/houses" ||
    seg === "/planetary-status" ||
    seg === "/compute" ||
    seg === "/ephemeris" ||
    seg === "/panchang"
  ) {
    return 1;
  }
  if (seg === "/three-council") return 4;
  if (seg === "/radio" || seg === "/sound") return 5;
  if (seg === "/dashas" || seg === "/dasha-analogs") return 2;
  return -1;
}

/**
 * Canonical destination for a legacy base route (chart id preserved by
 * the caller), or null when the route is not a legacy Watch route.
 */
export function legacyRedirectForPathname(pathname: string, chartId: string | null): string | null {
  const seg = "/" + (pathname.split("/")[1] || "");
  const target = LEGACY_ROUTE_REDIRECTS[seg];
  if (!target) return null;
  if (!chartId) return target === "/cast" ? target : "/cast";
  return `${target}/${chartId}`;
}

/** Public Watch chapters shown in the progress pathway (Winding is entry). */
export const PATHWAY_CHAPTER_IDS: readonly WatchChapterId[] = [1, 2, 4, 5, 6] as const;

// ── Personal vs demo helpers ────────────────────────────────────────────
// Re-exported here so callers can `import { isDemoChart, isPersonalChart }
// from "lib/journey"` and never reach into `lib/entitlements` directly.

export { isDemoChart } from "./entitlements";
export function isPersonalChart(chartId: string | null | undefined): boolean {
  if (!chartId) return false;
  if (isDemoChart(chartId)) return false;
  return true;
}
